import {
  ControlOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  RetweetOutlined
} from '@ant-design/icons';
import {useUser} from '@dofleini/security';
import {Button, message, Space} from 'antd';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQueryClient} from 'react-query';
import {useHistory} from 'react-router-dom';

import EntityMenu from '@/components/EntityMenu';
import {DIALOG_NAMESPACE} from '@/components/NewEmailDialog';
import Tooltip from '@/components/Tooltip';
import {useDialogsContext} from '@/contexts/DialogsContext';
import useCrud, {showConfirm} from '@/hooks/useCrud';
import {usePermissions} from '@/hooks/usePermissions';
import UserStatus from '@/modules/admin/components/UserStatus';
import {RESET_PASSWORD_DIALOG} from '@/modules/admin/containers/ResetPasswordModal';
import {USER_STATUS} from '@/modules/admin/contants/user.status';
import {CACHE_KEY_LIST_USER, ROUTE_PATH_USER, SECURITY_KEY_USER} from '@/modules/admin/contants/users';
import UserApiService from '@/modules/admin/services/UserApiService';
import {getUserFullName} from '@/utils/userFullName';

const options = (record, currentUser) => [
  {
    icon: <EyeOutlined/>,
    title: 'details',
    action: 'details',
    permissions: 'READ',
  },
  {
    icon: <EditOutlined/>,
    title: 'edit',
    action: 'edit',
    style: {minWidth: 160},
    permissions: 'WRITE',
  },
  {
    divider: true,
    permissions: 'WRITE',
  },
  {
    icon: <MailOutlined/>,
    title: 'sendEmail',
    action: 'sendEmail',
    disabled: record?.id === currentUser?.id,
    permissions: 'WRITE',
  },
  {
    icon: <RetweetOutlined/>,
    title: 'resetPassword',
    action: 'resetPassword',
    permissions: 'WRITE',
  },
  {
    icon: <ControlOutlined/>,
    title: 'user.changeStatus',
    action: 'changeStatus',
    disabled: record?.id === currentUser?.id,
    menu: [
      {
        title: <UserStatus status={USER_STATUS.ACTIVE} full help/>,
        action: 'changeStatus.active',
        style: {minWidth: 150}
      },
      {
        title: <UserStatus status={USER_STATUS.LOCK} full help/>,
        action: 'changeStatus.lock',
      },
    ],
    permissions: 'WRITE',
  },
  {
    divider: true,
    permissions: 'DELETE',
  },
  {
    icon: <DeleteOutlined/>,
    disabled: record?.id === currentUser?.id,
    title: 'remove',
    action: 'remove',
    permissions: 'DELETE',
  }
];

const useUserAction = (user, path = ROUTE_PATH_USER, securityKey = SECURITY_KEY_USER) => {
  const {openDialog} = useDialogsContext(RESET_PASSWORD_DIALOG);
  const {openDialog: showEmail} = useDialogsContext(DIALOG_NAMESPACE);
  const {push} = useHistory();
  const {user: loggedUser} = useUser();
  const queryClient = useQueryClient();
  const {optionsByPermissions} = usePermissions(securityKey, user, path);
  const {t} = useTranslation('admin');
  const {remove} = useCrud(UserApiService, CACHE_KEY_LIST_USER);
  const {mutateAsync: mutate, isLoading} = useMutation(UserApiService.updateStatus);

  const handleChange = useCallback(async (status) => {
    try {
      await mutate({users: [user._id], status});
      message.success(t('updateStatusSuccess', {status: t(`user.status.${status}`), user: getUserFullName(user)}));
      queryClient.invalidateQueries(CACHE_KEY_LIST_USER);
    } catch (e) {
      message.error('Error');
    }
  }, [mutate, queryClient, t, user]);

  const handleClick = useCallback(({action}) => {
    switch (action) {
      case 'details': {
        push(`${path}/${user?._id}`);
        break;
      }
      case 'edit': {
        push(`${path}/${user?._id}/edit`);
        break;
      }
      case 'resetPassword': {
        openDialog(user);
        break;
      }
      case 'sendEmail': {
        showEmail({users: [user]});
        break;
      }
      case 'changeStatus.active': {
        handleChange(USER_STATUS.ACTIVE);
        break;
      }
      case 'changeStatus.lock': {
        handleChange(USER_STATUS.LOCK);
        break;
      }
      case 'remove': {
        showConfirm(t, user.fullName, () => remove(user._id));
        break;
      }
      default: {
        console.log('missing action handler');
      }
    }
  }, [handleChange, openDialog, path, push, remove, showEmail, t, user]);

  const renderOptions = useMemo(() => {
    return optionsByPermissions(options(user, loggedUser), securityKey, {
      edit: true,
      details: true,
      sendEmail: true,
      resetPassword: true,
      changeStatus: true,
      remove: true,
    }, true);
  }, [loggedUser, optionsByPermissions, user, securityKey]);

  return {
    options: renderOptions,
    handleClick,
    isLoading,
    t
  };
};

const UserRowActions = ({user, path, securityKey}) => {
  const {isLoading, options, handleClick} = useUserAction(user, path, securityKey);
  return (
    <EntityMenu disabled={!options || !options.length} options={options} translate={'admin'} onClick={handleClick}
      isLoading={isLoading}/>
  );
};

// eslint-disable-next-line react/display-name
export const UserHeaderExtraActions = memo(({data}) => {
  const {isLoading, handleClick, t} = useUserAction(data);
  return (
    <Space>
      <Tooltip parentContainer title={t('sendEmail')} key={'sendEmail'}>
        <Button
          type="text"
          disabled={isLoading}
          onClick={() => handleClick({action: 'sendEmail'})}
          icon={<MailOutlined/>}/>
      </Tooltip>
      <Tooltip parentContainer title={t('resetPassword')} key={'resetPassword'}>
        <Button
          type="text"
          disabled={isLoading}
          onClick={() => handleClick({action: 'resetPassword'})}
          icon={<RetweetOutlined/>}/>
      </Tooltip>
    </Space>
  );
});

export default memo(UserRowActions);

const UserOptionCell = ({user, path, securityKey}) => {
  return (
    <UserRowActions user={user} path={path} securityKey={securityKey}/>
  );
};

UserOptionCell.propTypes = {
  user: PropTypes.object,
  path: PropTypes.string,
  securityKey: PropTypes.string,
};

/**
 * cellRender
 * */
/**
 * This is not a component,
 * it is a function to render the Status component in a table cell
 * @param {String} path this is the optional route
 * @param {String} securityKey this is the entityName value
 * */
// eslint-disable-next-line react/display-name
export const renderUserActions = (path, securityKey) => (id, user) => {
  return (
    <UserOptionCell user={user} path={path} securityKey={securityKey}/>
  );
};

UserRowActions.propTypes = {
  user: PropTypes.object,
  path: PropTypes.string,
  securityKey: PropTypes.string
};

UserRowActions.defaultProps = {
  path: '/users'
};

UserHeaderExtraActions.propTypes = {
  data: PropTypes.object
};
